// 그리드(표) 개인화 설정의 사용자측 저장소. 서버가 진짜 저장소고, 여기는
// (1) 로그인 후 첫 그리드가 뜰 때 전체를 한 번 불러와 메모리에 캐시하고,
// (2) 사용자별 localStorage 에도 거울로 남겨 다음 접속 때 서버 응답 전에도 바로 쓸 수 있게 하고,
// (3) 변경은 1초 debounce 후 PUT 하고, 초기화는 바로 DELETE 한다.
//
// 실패해도 그리드 동작을 막지 않는다 — 콘솔에 로그만 남기고 로컬 캐시로 계속 쓴다.
import { fetchGridPrefs, saveGridPref, deleteGridPref } from './api/gridPref';

const SAVE_DEBOUNCE_MS = 1000;

let cache = new Map(); // gridKey -> prefObject(파싱됨)
let primedForUser = null; // localStorage 거울을 메모리로 올려 둔 사용자
let loadedForUser = null; // 서버에서 전체를 받아온 사용자 uuid(또 로그인/사용자 전환 시 다시 받아오게)
let loadingPromise = null;
const listeners = new Set();
const pendingSaveTimers = new Map(); // gridKey -> timeout id

const currentUserUuid = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.usrUuid || null;
  } catch {
    return null;
  }
};

const mirrorKey = (usrUuid) => `gridPref:${usrUuid}`;

const readMirror = (usrUuid) => {
  try {
    const raw = localStorage.getItem(mirrorKey(usrUuid));
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const writeMirror = (usrUuid) => {
  try {
    const obj = {};
    cache.forEach((value, key) => { obj[key] = value; });
    localStorage.setItem(mirrorKey(usrUuid), JSON.stringify(obj));
  } catch {
    // 용량 초과/프라이빗 모드 등은 무시한다. 서버가 진짜 저장소이므로 거울이 없어도 치명적이지 않다.
  }
};

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // 리스너 쪽 에러가 저장 로직을 막지 않게
    }
  });
};

/** localStorage 거울을 메모리 캐시로 올린다. 이미 이 사용자로 올려 뒀으면 아무 것도 안 한다.
 * getPref 가 (ensureLoaded 를 아직 안 불렀어도) 항상 거울 값을 즉시 돌려줄 수 있게 한다. */
const primeFromMirror = (usrUuid) => {
  if (primedForUser === usrUuid) return;
  cache = new Map();
  const mirror = readMirror(usrUuid);
  Object.entries(mirror).forEach(([gridKey, pref]) => cache.set(gridKey, pref));
  primedForUser = usrUuid;
};

/**
 * 로그인 후 그리드를 처음 그릴 때 한 번만 서버 전체를 불러온다(지연 로딩).
 * 같은 사용자로 이미 불러왔으면 다시 부르지 않는다. 사용자가 바뀌면(로그아웃/다른 계정 로그인)
 * 다시 불러온다.
 */
export const ensureLoaded = () => {
  const usrUuid = currentUserUuid();
  if (!usrUuid) return Promise.resolve();

  if (loadedForUser === usrUuid) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  const wasPrimed = primedForUser === usrUuid;
  primeFromMirror(usrUuid);
  if (!wasPrimed && cache.size) notify();

  loadingPromise = fetchGridPrefs()
    .then((res) => {
      const list = res?.data?.list || [];
      list.forEach((row) => {
        try {
          cache.set(row.gridKey, JSON.parse(row.prefJson));
        } catch {
          // 서버에 잘못된 JSON 이 남아 있어도 그 줄만 건너뛴다.
        }
      });
      loadedForUser = usrUuid;
      writeMirror(usrUuid);
      notify();
    })
    .catch((err) => {
      console.error('그리드 개인화 설정을 불러오지 못했습니다(로컬 캐시로 동작):', err);
      // loadedForUser 를 세팅하지 않아 다음 마운트에서 다시 시도한다.
    })
    .finally(() => {
      loadingPromise = null;
    });

  return loadingPromise;
};

/** 캐시에서 즉시 읽는다(없으면 undefined). ensureLoaded 를 아직 안 불렀어도 localStorage
 * 거울은 동기로 먼저 채워서, 컴포넌트가 첫 렌더에서 바로 쓸 수 있게 한다. */
export const getPref = (gridKey) => {
  const usrUuid = currentUserUuid();
  if (usrUuid) primeFromMirror(usrUuid);
  return cache.get(gridKey);
};

/** 변경 구독. ensureLoaded 완료/저장/초기화마다 호출된다. 구독 해제 함수를 돌려준다. */
export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** 바로 캐시+거울에 반영하고, 서버 저장은 1초 묶어서(debounce) 보낸다. */
export const savePref = (gridKey, pref) => {
  if (!gridKey || !pref) return;
  cache.set(gridKey, pref);
  const usrUuid = currentUserUuid();
  if (usrUuid) writeMirror(usrUuid);
  notify();

  const existingTimer = pendingSaveTimers.get(gridKey);
  if (existingTimer) clearTimeout(existingTimer);
  const timer = setTimeout(() => {
    pendingSaveTimers.delete(gridKey);
    saveGridPref(gridKey, JSON.stringify(pref)).catch((err) => {
      console.error('그리드 개인화 설정 저장 실패(다음 변경 때 다시 시도됨):', err);
    });
  }, SAVE_DEBOUNCE_MS);
  pendingSaveTimers.set(gridKey, timer);
};

/** "기본값으로": 캐시/거울에서 지우고 서버에도 삭제 요청. 코드 기본 레이아웃으로 돌아간다. */
export const resetPref = (gridKey) => {
  if (!gridKey) return;
  cache.delete(gridKey);
  const existingTimer = pendingSaveTimers.get(gridKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
    pendingSaveTimers.delete(gridKey);
  }
  const usrUuid = currentUserUuid();
  if (usrUuid) writeMirror(usrUuid);
  notify();

  deleteGridPref(gridKey).catch((err) => {
    console.error('그리드 개인화 설정 초기화(서버) 실패:', err);
  });
};
